import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type {
  LessonCaptionCue,
  LessonCaptionStatus,
  LessonCaptionTrack,
} from "@/lib/lessons/lessonCaptionTypes";

const PREF_KEY = "heirwise.lessonCaptions.enabled";

function readPref(): boolean {
  try {
    const raw = localStorage.getItem(PREF_KEY);
    if (raw === "false") return false;
    if (raw === "true") return true;
  } catch { /* ignore */ }
  return true; // default ON for accessibility
}

function isCue(v: unknown): v is LessonCaptionCue {
  if (!v || typeof v !== "object") return false;
  const c = v as Record<string, unknown>;
  return typeof c.startTime === "number" && typeof c.endTime === "number" && typeof c.text === "string";
}

/**
 * Lesson video closed captions.
 *
 * - Loads the caption track (if any) for the given lessonId.
 * - Tracks the learner's CC on/off preference in localStorage.
 * - Returns the active cue for the supplied currentTime, falling back
 *   to a transcript snippet (proportional to playback progress) when no
 *   timed cues exist but a fallback transcript is available.
 * - Missing data is a soft-fail: the hook simply reports `status: "missing"`.
 */
export function useLessonCaptions(lessonId: string | undefined, currentTime: number, duration: number) {
  const [enabled, setEnabledState] = useState<boolean>(() => readPref());
  const [track, setTrack] = useState<LessonCaptionTrack | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const loggedMissing = useRef<Set<string>>(new Set());

  const setEnabled = useCallback((v: boolean) => {
    setEnabledState(v);
    try { localStorage.setItem(PREF_KEY, String(v)); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!lessonId) { setTrack(null); return; }
    setLoading(true);
    (async () => {
      try {
        const { data, error } = await supabase
          .from("lesson_captions")
          .select("lesson_id, instructor, cues, fallback_transcript, source")
          .eq("lesson_id", lessonId)
          .maybeSingle();
        if (cancelled) return;
        if (error || !data) {
          if (!loggedMissing.current.has(lessonId)) {
            loggedMissing.current.add(lessonId);
            console.warn("[lesson-captions] no track for", lessonId);
          }
          setTrack(null);
          return;
        }
        const rawCues: unknown[] = Array.isArray(data.cues) ? (data.cues as unknown[]) : [];
        const cues: LessonCaptionCue[] = rawCues.filter(isCue);
        setTrack({
          lessonId,
          instructor: (data.instructor as LessonCaptionTrack["instructor"]) ?? null,
          cues,
          fallbackTranscript: data.fallback_transcript ?? null,
          source: data.source ?? "manual",
        });
      } catch (e) {
        if (!cancelled) setTrack(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lessonId]);

  const status: LessonCaptionStatus = useMemo(() => {
    if (!track) return "missing";
    if (track.cues.length > 0) return "available";
    if (track.fallbackTranscript) return "fallback";
    return "missing";
  }, [track]);

  const activeCue: string | null = useMemo(() => {
    if (!enabled || !track) return null;
    if (track.cues.length > 0) {
      const cue = track.cues.find((c) => currentTime >= c.startTime && currentTime <= c.endTime);
      return cue?.text ?? null;
    }
    if (track.fallbackTranscript && duration > 0) {
      // Chunk transcript into ~120-char segments and advance proportionally.
      const chunks = chunkTranscript(track.fallbackTranscript, 120);
      if (chunks.length === 0) return null;
      const idx = Math.min(chunks.length - 1, Math.floor((currentTime / duration) * chunks.length));
      return chunks[Math.max(0, idx)];
    }
    return null;
  }, [enabled, track, currentTime, duration]);

  return {
    enabled,
    setEnabled,
    track,
    status,
    activeCue,
    hasCaptions: status !== "missing",
    loading,
  };
}

function chunkTranscript(text: string, maxLen: number): string[] {
  const clean = text.replace(/\s+/g, " ").trim();
  if (!clean) return [];
  const sentences = clean.split(/(?<=[.!?])\s+/);
  const chunks: string[] = [];
  let buf = "";
  for (const s of sentences) {
    if ((buf + " " + s).trim().length > maxLen && buf) {
      chunks.push(buf.trim());
      buf = s;
    } else {
      buf = (buf + " " + s).trim();
    }
  }
  if (buf) chunks.push(buf.trim());
  return chunks;
}
